import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { db, auth } from './firebase';
import { supabase } from './supabase';
import {
  collection, addDoc, onSnapshot, query, orderBy,
  deleteDoc, doc, getDocs, where, writeBatch, invalidateTable
} from './firestore-compat';

// ── Parsers ──────────────────────────────────────────────────────────────────
function parsePlanilhaDinamica(workbook) {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  if (!raw || raw.length === 0) return { rows: [], colunas: [] };
  const colunas = Object.keys(raw[0]);
  const rows = raw.map(r => {
    const obj = {};
    colunas.forEach(col => {
      let val = r[col];
      if (typeof val === 'number' && val > 1 && val < 2958466 && Number.isInteger(val)) {
        const isDateCol = /data|nasc|admiss|alta|registro|saida|saída|dt\s|inicio|fim/i.test(col);
        if (isDateCol) {
          try {
            const d = XLSX.SSF.parse_date_code(val);
            if (d && d.y >= 1900) val = `${String(d.d).padStart(2,'0')}/${String(d.m).padStart(2,'0')}/${d.y}`;
          } catch (e) {}
        }
      }
      obj[col] = (val === null || val === undefined) ? '' : String(val).trim();
    });
    return obj;
  });
  return { rows, colunas };
}

function detectColMerge(colunas, keywords) {
  return colunas.find(c => keywords.some(k => c.toLowerCase().includes(k))) || '';
}

// Gera chave de correspondência — prioridade: numAtend (único), fallback: nome+nasc
function makeKey(numAtend, nome, nasc, usarNumAtend) {
  if (usarNumAtend && numAtend && String(numAtend).trim()) {
    return `atend::${String(numAtend).trim()}`;
  }
  const n = String(nome || '').toLowerCase().trim().replace(/\s+/g, ' ');
  const d = String(nasc || '').trim();
  if (!n && !d) return null;
  return `nomenasc::${n}||${d}`;
}

function mesclarPlanilhas(
  rowsPrincipal, rowsSecundaria,
  colNumAtendPrinc, colNomePrinc, colNascPrinc,
  colNumAtendSecund, colNomeSecund, colNascSecund
) {
  const usarNumAtend = !!(colNumAtendPrinc && colNumAtendSecund);

  const keysPrincipal = new Map();
  rowsPrincipal.forEach((r, idx) => {
    const k = makeKey(r[colNumAtendPrinc], r[colNomePrinc], r[colNascPrinc], usarNumAtend);
    if (k) keysPrincipal.set(k, idx);
  });

  const adicionados = [];
  const coincidentes = [];

  rowsSecundaria.forEach(r => {
    const k = makeKey(r[colNumAtendSecund], r[colNomeSecund], r[colNascSecund], usarNumAtend);
    if (!k) return;
    if (keysPrincipal.has(k)) {
      coincidentes.push(r);
    } else {
      adicionados.push(r);
    }
  });

  // Normaliza colunas dos adicionados para coincidir com a planilha principal
  const colunasBase = rowsPrincipal.length > 0 ? Object.keys(rowsPrincipal[0]) : [];
  const adicionadosNormalizados = adicionados.map(r => {
    const novo = {};
    colunasBase.forEach(c => { novo[c] = ''; });
    // Mapeia campos da secundária para a principal pelo nome da coluna (case-insensitive)
    const keysSecund = Object.keys(r);
    colunasBase.forEach(c => {
      const match = keysSecund.find(k => k.toLowerCase().trim() === c.toLowerCase().trim());
      if (match) novo[c] = r[match];
    });
    // Garante que nome e nasc estejam corretos
    if (colNomePrinc && colNomeSecund) novo[colNomePrinc] = r[colNomeSecund] || '';
    if (colNascPrinc && colNascSecund) novo[colNascPrinc] = r[colNascSecund] || '';
    return novo;
  });

  return {
    merged: [...rowsPrincipal, ...adicionadosNormalizados],
    stats: {
      principal: rowsPrincipal.length,
      secundaria: rowsSecundaria.length,
      coincidentes: coincidentes.length,
      adicionados: adicionados.length,
      total: rowsPrincipal.length + adicionados.length,
    },
    preview: adicionados.slice(0, 10),
    estrategia: usarNumAtend ? 'numero_atendimento' : 'nome_nascimento',
  };
}

// ── Firebase ─────────────────────────────────────────────────────────────────
async function salvarRowsEmLotes(uploadDocId, rows) {
  const CHUNK = 400;
  for (let i = 0; i < rows.length; i += CHUNK) {
    const batch = writeBatch(db);
    rows.slice(i, i + CHUNK).forEach((row, j) => {
      const rowRef = doc(collection(db, 'indicadores_uploads', uploadDocId, 'rows'));
      batch.set(rowRef, { ...row, _idx: i + j });
    });
    await batch.commit();
  }
}

async function deletarRowsDaSubcolecao(uploadDocId) {
  const rowsRef = collection(db, 'indicadores_uploads', uploadDocId, 'rows');
  const snap = await getDocs(rowsRef);
  if (snap.empty) return;
  const CHUNK = 400;
  for (let i = 0; i < snap.docs.length; i += CHUNK) {
    const batch = writeBatch(db);
    snap.docs.slice(i, i + CHUNK).forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
}

// ── Estilos ───────────────────────────────────────────────────────────────────
const P = {
  blue: '#2563EB', green: '#10B981', orange: '#F59E0B',
  red: '#EF4444', purple: '#8B5CF6', teal: '#0D9488',
  text: '#1E293B', muted: '#64748B', bg: '#F4F6FB', card: '#FFFFFF',
};

const s = {
  page: { minHeight: '100vh', background: P.bg, padding: '32px 28px', fontFamily: "'Inter','Segoe UI',sans-serif", color: P.text },
  card: (border) => ({ background: P.card, borderRadius: 16, padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.08)', borderLeft: border ? `4px solid ${border}` : undefined }),
  title: { margin: 0, fontSize: '1.5rem', fontWeight: 800, color: P.text },
  sub: { margin: '4px 0 0', fontSize: '0.88rem', color: P.muted },
  cardTitle: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: P.muted, marginBottom: 14 },
  dropzone: (d, hasFile) => ({
    border: `2px dashed ${d ? P.blue : hasFile ? P.green : '#CBD5E1'}`,
    borderRadius: 12, padding: '32px 20px', textAlign: 'center',
    cursor: 'pointer', transition: 'all 0.2s',
    background: d ? `${P.blue}08` : hasFile ? `${P.green}06` : 'transparent',
  }),
  btn: (color, disabled) => ({
    padding: '11px 22px', borderRadius: 10, border: 'none',
    fontWeight: 700, fontSize: '0.9rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    background: color === 'green' ? `linear-gradient(135deg,${P.green},#059669)`
      : color === 'blue' ? `linear-gradient(135deg,${P.blue},#1D4ED8)`
      : color === 'red' ? `${P.red}18` : '#F1F5F9',
    color: color === 'red' ? P.red : '#fff',
    transition: 'all 0.2s',
    width: color === 'green' || color === 'blue' ? '100%' : 'auto',
    marginTop: color === 'green' || color === 'blue' ? 14 : 0,
  }),
  alert: (type) => ({
    padding: '12px 16px', borderRadius: 10, marginBottom: 14,
    fontSize: '0.88rem', fontWeight: 600,
    background: type === 'success' ? `${P.green}15` : type === 'info' ? `${P.blue}10` : `${P.red}15`,
    border: `1px solid ${type === 'success' ? `${P.green}40` : type === 'info' ? `${P.blue}30` : `${P.red}40`}`,
    color: type === 'success' ? P.green : type === 'info' ? P.blue : P.red,
  }),
  sel: { padding: '8px 10px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#F8FAFC', color: P.text, fontSize: '0.83rem', outline: 'none', width: '100%' },
  th: { padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #F1F5F9', color: P.muted, fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', whiteSpace: 'nowrap', background: '#F8FAFC' },
  td: { padding: '8px 10px', borderBottom: '1px solid #F8FAFC', color: P.text, fontSize: '0.8rem', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' },
  tag: { display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700, background: `${P.green}15`, color: P.green, border: `1px solid ${P.green}30`, marginRight: 4, marginBottom: 4 },
  statBox: (color) => ({ background: `${color}10`, border: `1px solid ${color}25`, borderRadius: 10, padding: '12px 16px', textAlign: 'center' }),
};

// ── Sub-componente seletor de coluna ──────────────────────────────────────────
function ColSelector({ label, badge, value, onChange, colunas }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <label style={{ fontSize: '0.75rem', color: P.muted, fontWeight: 600 }}>{label}</label>
        {badge === 'auto' && <span style={{ fontSize: '0.65rem', background: `${P.green}18`, color: P.green, border: `1px solid ${P.green}30`, borderRadius: 20, padding: '1px 7px', fontWeight: 700 }}>auto</span>}
        {badge === 'Recomendado' && <span style={{ fontSize: '0.65rem', background: `${P.blue}15`, color: P.blue, border: `1px solid ${P.blue}25`, borderRadius: 20, padding: '1px 7px', fontWeight: 700 }}>recomendado</span>}
      </div>
      <select style={s.sel} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">— Não mapeado —</option>
        {colunas.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      {value && <div style={{ fontSize: '0.68rem', color: P.green, marginTop: 3 }}>✔ {value}</div>}
    </div>
  );
}

// ── Componente ────────────────────────────────────────────────────────────────
export default function UploadIndicadores() {
  // Planilha principal
  const [filePrinc, setFilePrinc]           = useState(null);
  const [rowsPrinc, setRowsPrinc]           = useState([]);
  const [colunasPrinc, setColunasPrinc]     = useState([]);
  const [dragPrinc, setDragPrinc]           = useState(false);
  const fileRefPrinc = useRef();

  // Planilha secundária
  const [fileSecund, setFileSecund]         = useState(null);
  const [rowsSecund, setRowsSecund]         = useState([]);
  const [colunasSecund, setColunasSecund]   = useState([]);
  const [dragSecund, setDragSecund]         = useState(false);
  const fileRefSecund = useRef();

  // Mapeamento de colunas para merge
  const [colNumAtendPrinc, setColNumAtendPrinc] = useState('');
  const [colNomePrinc, setColNomePrinc]         = useState('');
  const [colNascPrinc, setColNascPrinc]         = useState('');
  const [colNumAtendSecund, setColNumAtendSecund] = useState('');
  const [colNomeSecund, setColNomeSecund]       = useState('');
  const [colNascSecund, setColNascSecund]       = useState('');

  // Resultado da mesclagem
  const [mergeResult, setMergeResult]       = useState(null);

  // Upload
  const [uploading, setUploading]           = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [history, setHistory]               = useState([]);
  const [userName, setUserName]             = useState('');
  const [isAdmin, setIsAdmin]               = useState(false);
  const [deleting, setDeleting]             = useState(null);
  const [successMsg, setSuccessMsg]         = useState('');
  const [errorMsg, setErrorMsg]             = useState('');
  const [modoMescla, setModoMescla]         = useState(false);
  const [ftpSyncing, setFtpSyncing]         = useState(false);

  const handleFTPSync = async () => {
    // A rota /api/pull-ftp só existe no Vercel (produção).
    // Em localhost o CRA devolve index.html → erro de JSON.
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      setErrorMsg('A sincronização com FTP não está disponível localmente. Acesse gestaoquality.com.br para usar esta funcionalidade.');
      return;
    }

    setFtpSyncing(true);
    setSuccessMsg('');
    setErrorMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Sessão expirada. Faça login novamente.');

      const resp = await fetch('/api/pull-ftp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      // Verifica se a resposta é realmente JSON antes de parsear
      const ct = resp.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        await resp.text();
        throw new Error(`Resposta inesperada do servidor (${resp.status}). Verifique os logs da função no Vercel.`);
      }

      const body = await resp.json();
      if (!resp.ok) {
        throw new Error(body.error || 'Erro na sincronização do FTP.');
      }

      setSuccessMsg(`✅ Sincronização FTP concluída! ${body.stats.totalRows.toLocaleString('pt-BR')} registros importados.`);
      setTimeout(() => setSuccessMsg(''), 8000);
      // Notifica onSnapshot do dashboard para re-buscar imediatamente
      invalidateTable('indicadores_uploads');
    } catch (err) {
      setErrorMsg(`Erro na sincronização com FTP: ${err.message}`);
    }
    setFtpSyncing(false);
  };

  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;
    getDocs(query(collection(db, 'users'), where('email', '==', currentUser.email))).then(snap => {
      if (!snap.empty) {
        const d = snap.docs[0].data();
        setUserName(d.name || currentUser.email);
        const roles = Array.isArray(d.roles) ? d.roles : (d.role ? [d.role] : []);
        setIsAdmin(roles.includes('admin') || roles.includes('administrador'));
      }
    });
  }, [currentUser]);

  useEffect(() => {
    const q = query(collection(db, 'indicadores_uploads'), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  // Auto-detecta colunas quando planilhas são carregadas
  useEffect(() => {
    if (colunasPrinc.length > 0) {
      setColNumAtendPrinc(detectColMerge(colunasPrinc, ['num atend','nº atend','numero atend','número atend','cod atend','id atend','atendimento','prontuário','prontuario','cod pac','id pac']));
      setColNomePrinc(detectColMerge(colunasPrinc, ['nome','paciente','name']));
      setColNascPrinc(detectColMerge(colunasPrinc, ['nascimento','nasc','dt nasc','data nasc','dob']));
    }
  }, [colunasPrinc]);

  useEffect(() => {
    if (colunasSecund.length > 0) {
      setColNumAtendSecund(detectColMerge(colunasSecund, ['num atend','nº atend','numero atend','número atend','cod atend','id atend','atendimento','prontuário','prontuario','cod pac','id pac']));
      setColNomeSecund(detectColMerge(colunasSecund, ['nome','paciente','name']));
      setColNascSecund(detectColMerge(colunasSecund, ['nascimento','nasc','dt nasc','data nasc','dob']));
    }
  }, [colunasSecund]);

  // Recalcula mesclagem sempre que mudar planilha ou mapeamento
  useEffect(() => {
    if (!modoMescla || rowsPrinc.length === 0 || rowsSecund.length === 0) {
      setMergeResult(null);
      return;
    }
    // Mínimo: nome + nasc OR numAtend em ambas
    const temNumAtend = colNumAtendPrinc && colNumAtendSecund;
    const temNomeNasc = colNomePrinc && colNascPrinc && colNomeSecund && colNascSecund;
    if (!temNumAtend && !temNomeNasc) {
      setMergeResult(null);
      return;
    }
    const result = mesclarPlanilhas(
      rowsPrinc, rowsSecund,
      colNumAtendPrinc, colNomePrinc, colNascPrinc,
      colNumAtendSecund, colNomeSecund, colNascSecund
    );
    setMergeResult(result);
  }, [modoMescla, rowsPrinc, rowsSecund, colNumAtendPrinc, colNomePrinc, colNascPrinc, colNumAtendSecund, colNomeSecund, colNascSecund]);

  const processFile = useCallback((f, tipo) => {
    if (!f) return;
    if (!f.name.match(/\.(xlsx|xls|csv)$/i)) { setErrorMsg('Apenas .xlsx, .xls ou .csv são aceitos.'); return; }
    setErrorMsg('');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: false });
        const { rows, colunas } = parsePlanilhaDinamica(wb);
        if (tipo === 'principal') {
          setFilePrinc(f); setRowsPrinc(rows); setColunasPrinc(colunas);
        } else {
          setFileSecund(f); setRowsSecund(rows); setColunasSecund(colunas);
        }
      } catch (err) {
        setErrorMsg('Erro ao ler o arquivo. Verifique se é uma planilha válida.');
      }
    };
    reader.readAsArrayBuffer(f);
  }, []);

  const handleUpload = async () => {
    const rowsParaSalvar = modoMescla && mergeResult ? mergeResult.merged : rowsPrinc;
    const fileName = modoMescla && mergeResult
      ? `${filePrinc.name} + ${fileSecund?.name} [MESCLADO]`
      : filePrinc.name;

    if (!rowsParaSalvar.length) return;
    setUploading(true); setUploadProgress(0); setErrorMsg('');
    try {
      const today = new Date().toISOString().split('T')[0];
      const safeRows = JSON.parse(JSON.stringify(rowsParaSalvar));
      setUploadProgress(10);

      const q = query(collection(db, 'indicadores_uploads'), where('dataReferencia', '==', today));
      const existing = await getDocs(q);
      for (const d of existing.docs) {
        await deletarRowsDaSubcolecao(d.id);
        await deleteDoc(doc(db, 'indicadores_uploads', d.id));
      }
      setUploadProgress(25);

      const colunasFinal = rowsParaSalvar.length > 0 ? Object.keys(rowsParaSalvar[0]).filter(c => c !== '_idx') : colunasPrinc;

      const metadoc = await addDoc(collection(db, 'indicadores_uploads'), {
        timestamp:      new Date().toISOString(),
        dataReferencia: today,
        uploadedBy:     userName,
        uploadedByUid:  currentUser?.uid || 'unknown',
        fileName,
        totalRows:      safeRows.length,
        colunas:        colunasFinal,
        storageMode:    'subcollection',
        modoMescla:     modoMescla && !!mergeResult,
        mergeStats:     modoMescla && mergeResult ? { ...mergeResult.stats, estrategia: mergeResult.estrategia } : null,
      });
      setUploadProgress(35);
      await salvarRowsEmLotes(metadoc.id, safeRows);
      setUploadProgress(100);

      const repl = existing.size > 0 ? ` (substituiu upload anterior do dia)` : '';
      setSuccessMsg(`✅ ${safeRows.length.toLocaleString('pt-BR')} registros salvos!${repl}`);
      setFilePrinc(null); setRowsPrinc([]); setColunasPrinc([]);
      setFileSecund(null); setRowsSecund([]); setColunasSecund([]);
      setMergeResult(null); setModoMescla(false);
      setTimeout(() => setSuccessMsg(''), 8000);
    } catch (err) {
      setErrorMsg(`Erro ao salvar: ${err.message}`);
    }
    setUploading(false); setUploadProgress(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir este upload e todos os seus dados?')) return;
    setDeleting(id);
    try {
      await deletarRowsDaSubcolecao(id);
      await deleteDoc(doc(db, 'indicadores_uploads', id));
    } catch (err) { console.error(err); }
    setDeleting(null);
  };

  const rowsParaSalvar = modoMescla && mergeResult ? mergeResult.merged : rowsPrinc;
  const prontoParaSalvar = rowsPrinc.length > 0 && (!modoMescla || !!mergeResult);

  return (
    <div style={s.page}>

      {/* ── CABEÇALHO ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <div style={{ width: 52, height: 52, background: `linear-gradient(135deg,${P.green},#059669)`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', boxShadow: `0 4px 20px ${P.green}40` }}>📊</div>
        <div>
          <h1 style={s.title}>Upload de Indicadores</h1>
          <p style={s.sub}>Envie uma planilha ou mescle duas para unificar históricos de pacientes</p>
        </div>
      </div>

      {successMsg && <div style={s.alert('success')}>{successMsg}</div>}
      {errorMsg   && <div style={s.alert('error')}>{errorMsg}</div>}

      {/* ── TOGGLE MODO ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <button onClick={() => setModoMescla(false)}
          style={{ ...s.btn(modoMescla ? '' : 'blue', false), width: 'auto', marginTop: 0, fontSize: '0.85rem', padding: '9px 20px', background: !modoMescla ? `linear-gradient(135deg,${P.blue},#1D4ED8)` : '#F1F5F9', color: !modoMescla ? '#fff' : P.muted }}>
          📄 Planilha única
        </button>
        <button onClick={() => setModoMescla(true)}
          style={{ ...s.btn(modoMescla ? 'blue' : '', false), width: 'auto', marginTop: 0, fontSize: '0.85rem', padding: '9px 20px', background: modoMescla ? `linear-gradient(135deg,${P.purple},#7C3AED)` : '#F1F5F9', color: modoMescla ? '#fff' : P.muted }}>
          🔀 Mesclar duas planilhas
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: modoMescla ? '1fr 1fr' : '420px 1fr', gap: 20, maxWidth: 1400 }}>

        {/* ── COLUNA ESQUERDA ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Sincronização via FTP */}
          <div style={s.card(P.green)}>
            <div style={s.cardTitle}>🔄 Sincronização Automática (FTP IW)</div>
            <p style={{ fontSize: '0.82rem', color: P.muted, margin: '0 0 14px 0', lineHeight: 1.5 }}>
              O sistema sincroniza com o FTP da IW automaticamente todos os dias às 06h. Para forçar uma atualização manual agora, clique no botão abaixo.
            </p>

            {/* Barra de progresso animada */}
            {ftpSyncing && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: P.green, fontWeight: 700, marginBottom: 6 }}>
                  <span>⏳ Baixando dados do FTP e salvando no banco...</span>
                  <span>Aguarde</span>
                </div>
                <div style={{ background: '#E2E8F0', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    borderRadius: 99,
                    background: `linear-gradient(90deg, ${P.green}, #059669)`,
                    animation: 'ftpProgress 2s ease-in-out infinite',
                    width: '60%',
                  }} />
                </div>
                <style>{`
                  @keyframes ftpProgress {
                    0%   { width: 10%; margin-left: 0%; }
                    50%  { width: 60%; margin-left: 20%; }
                    100% { width: 10%; margin-left: 90%; }
                  }
                `}</style>
              </div>
            )}

            <button
              onClick={handleFTPSync}
              disabled={ftpSyncing}
              style={{
                ...s.btn(ftpSyncing ? '' : 'green', ftpSyncing),
                marginTop: 0,
                width: '100%'
              }}
            >
              {ftpSyncing ? '⏳ Sincronizando...' : '🔄 Sincronizar com FTP Agora'}
            </button>
          </div>

          {/* Planilha Principal */}
          <div style={s.card(P.blue)}>
            <div style={s.cardTitle}>📁 {modoMescla ? 'Planilha Principal (atual / mais recente)' : 'Selecionar Arquivo'}</div>
            <div style={{ background: `${P.blue}08`, border: `1px solid ${P.blue}20`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.83rem', color: P.blue }}>
              {modoMescla
                ? '📌 Esta planilha tem prioridade — quando o mesmo paciente aparecer nas duas, os dados desta serão mantidos.'
                : '🔄 Ao enviar uma nova planilha hoje, os dados anteriores do dia são substituídos automaticamente.'}
            </div>

            <div style={s.dropzone(dragPrinc, !!filePrinc)}
              onDragOver={e => { e.preventDefault(); setDragPrinc(true); }}
              onDragLeave={() => setDragPrinc(false)}
              onDrop={e => { e.preventDefault(); setDragPrinc(false); processFile(e.dataTransfer.files[0], 'principal'); }}
              onClick={() => fileRefPrinc.current.click()}>
              <div style={{ fontSize: '2.8rem', marginBottom: 8 }}>{filePrinc ? '✅' : '📋'}</div>
              <div style={{ fontWeight: 700, color: P.text, marginBottom: 4 }}>
                {filePrinc ? filePrinc.name : 'Arraste ou clique para selecionar'}
              </div>
              {filePrinc
                ? <div style={{ fontSize: '0.83rem', color: P.green, fontWeight: 600 }}>{rowsPrinc.length.toLocaleString('pt-BR')} registros · {colunasPrinc.length} colunas ✔</div>
                : <div style={{ fontSize: '0.8rem', color: P.muted }}>Formatos: .xlsx, .xls, .csv</div>}
              <input ref={fileRefPrinc} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={e => processFile(e.target.files[0], 'principal')} />
            </div>

            {colunasPrinc.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  {colunasPrinc.length} colunas detectadas:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {colunasPrinc.map(c => <span key={c} style={s.tag}>{c}</span>)}
                </div>
              </div>
            )}
          </div>

          {/* Planilha Secundária (só no modo mescla) */}
          {modoMescla && (
            <div style={s.card(P.purple)}>
              <div style={s.cardTitle}>📁 Planilha Secundária (histórica / anterior)</div>
              <div style={{ background: `${P.purple}08`, border: `1px solid ${P.purple}20`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.83rem', color: P.purple }}>
                📚 Pacientes desta planilha que NÃO estiverem na principal serão adicionados ao resultado final.
              </div>

              <div style={s.dropzone(dragSecund, !!fileSecund)}
                onDragOver={e => { e.preventDefault(); setDragSecund(true); }}
                onDragLeave={() => setDragSecund(false)}
                onDrop={e => { e.preventDefault(); setDragSecund(false); processFile(e.dataTransfer.files[0], 'secundaria'); }}
                onClick={() => fileRefSecund.current.click()}>
                <div style={{ fontSize: '2.8rem', marginBottom: 8 }}>{fileSecund ? '✅' : '📚'}</div>
                <div style={{ fontWeight: 700, color: P.text, marginBottom: 4 }}>
                  {fileSecund ? fileSecund.name : 'Arraste ou clique para selecionar'}
                </div>
                {fileSecund
                  ? <div style={{ fontSize: '0.83rem', color: P.green, fontWeight: 600 }}>{rowsSecund.length.toLocaleString('pt-BR')} registros · {colunasSecund.length} colunas ✔</div>
                  : <div style={{ fontSize: '0.8rem', color: P.muted }}>Formatos: .xlsx, .xls, .csv</div>}
                <input ref={fileRefSecund} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={e => processFile(e.target.files[0], 'secundaria')} />
              </div>

              {colunasSecund.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    {colunasSecund.length} colunas detectadas:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {colunasSecund.map(c => <span key={c} style={{ ...s.tag, background: `${P.purple}15`, color: P.purple, border: `1px solid ${P.purple}30` }}>{c}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Histórico */}
          <div style={s.card()}>
            <div style={s.cardTitle}>🕓 Histórico de Uploads</div>
            {history.length === 0 && <div style={{ color: P.muted, fontSize: '0.85rem' }}>Nenhum upload realizado ainda.</div>}
            {history.map(h => (
              <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: 10, background: '#F8FAFC', border: '1px solid #F1F5F9', marginBottom: 8, gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, color: P.text, fontSize: '0.86rem', marginBottom: 2 }}>📄 {h.fileName}</div>
                  <div style={{ fontSize: '0.76rem', color: P.muted }}>
                    {h.dataReferencia} · {h.totalRows?.toLocaleString('pt-BR')} registros · {h.uploadedBy}
                  </div>
                  {h.modoMescla && h.mergeStats && (
                    <div style={{ fontSize: '0.72rem', color: P.purple, marginTop: 3, fontWeight: 600 }}>
                      🔀 Mesclado: {h.mergeStats.principal} + {h.mergeStats.adicionados} adicionados = {h.mergeStats.total} total
                      {h.mergeStats.estrategia === 'numero_atendimento'
                        ? <span style={{ color: P.green, marginLeft: 6 }}>🎯 chave: Nº Atendimento</span>
                        : <span style={{ color: P.orange, marginLeft: 6 }}>👤 chave: Nome + Nasc.</span>}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <button style={{ ...s.btn('red', deleting === h.id), width: 'auto', marginTop: 0, padding: '6px 12px' }}
                    disabled={deleting === h.id} onClick={() => handleDelete(h.id)}>
                    {deleting === h.id ? '⏳' : '🗑️'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── COLUNA DIREITA ────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Configuração do merge */}
          {modoMescla && rowsPrinc.length > 0 && rowsSecund.length > 0 && (
            <div style={s.card()}>
              <div style={s.cardTitle}>🔗 Configurar Chave de Correspondência</div>

              {/* Badge da estratégia ativa */}
              {mergeResult && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', borderRadius: 10,
                  background: mergeResult.estrategia === 'numero_atendimento' ? `${P.green}12` : `${P.orange}10`,
                  border: `1px solid ${mergeResult.estrategia === 'numero_atendimento' ? P.green : P.orange}30` }}>
                  <span style={{ fontSize: '1.2rem' }}>{mergeResult.estrategia === 'numero_atendimento' ? '🎯' : '⚠️'}</span>
                  <div style={{ fontSize: '0.83rem', fontWeight: 600, color: mergeResult.estrategia === 'numero_atendimento' ? P.green : '#92400E' }}>
                    {mergeResult.estrategia === 'numero_atendimento'
                      ? 'Usando Nº de Atendimento como chave principal — identificação mais precisa'
                      : 'Usando Nome + Data de Nascimento como chave — Nº de Atendimento não detectado nas duas planilhas'}
                  </div>
                </div>
              )}

              {!mergeResult && (
                <div style={{ background: `${P.orange}10`, border: `1px solid ${P.orange}25`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.83rem', color: '#92400E' }}>
                  🎯 <strong>Prioridade:</strong> se o <strong>Nº de Atendimento</strong> estiver mapeado nas duas planilhas, ele será usado como chave principal.
                  Caso contrário, usa <strong>Nome + Data de Nascimento</strong>.
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Planilha Principal */}
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: P.blue, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: P.blue, display: 'inline-block' }} />
                    Planilha Principal
                  </div>

                  <ColSelector label="🎯 Nº de Atendimento (chave única)" badge="Recomendado"
                    value={colNumAtendPrinc} onChange={setColNumAtendPrinc} colunas={colunasPrinc} />
                  <ColSelector label="👤 Nome do Paciente" badge={colNomePrinc ? 'auto' : ''}
                    value={colNomePrinc} onChange={setColNomePrinc} colunas={colunasPrinc} />
                  <ColSelector label="🎂 Data de Nascimento" badge={colNascPrinc ? 'auto' : ''}
                    value={colNascPrinc} onChange={setColNascPrinc} colunas={colunasPrinc} />
                </div>

                {/* Planilha Secundária */}
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: P.purple, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: P.purple, display: 'inline-block' }} />
                    Planilha Secundária
                  </div>

                  <ColSelector label="🎯 Nº de Atendimento (chave única)" badge="Recomendado"
                    value={colNumAtendSecund} onChange={setColNumAtendSecund} colunas={colunasSecund} />
                  <ColSelector label="👤 Nome do Paciente" badge={colNomeSecund ? 'auto' : ''}
                    value={colNomeSecund} onChange={setColNomeSecund} colunas={colunasSecund} />
                  <ColSelector label="🎂 Data de Nascimento" badge={colNascSecund ? 'auto' : ''}
                    value={colNascSecund} onChange={setColNascSecund} colunas={colunasSecund} />
                </div>
              </div>
            </div>
          )}

          {/* Resultado da mesclagem */}
          {modoMescla && mergeResult && (
            <div style={s.card()}>
              <div style={s.cardTitle}>📊 Resultado da Mesclagem</div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={s.statBox(P.blue)}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: P.blue }}>{mergeResult.stats.principal.toLocaleString('pt-BR')}</div>
                  <div style={{ fontSize: '0.72rem', color: P.muted, marginTop: 3 }}>Planilha principal</div>
                </div>
                <div style={s.statBox(P.orange)}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: P.orange }}>{mergeResult.stats.coincidentes.toLocaleString('pt-BR')}</div>
                  <div style={{ fontSize: '0.72rem', color: P.muted, marginTop: 3 }}>Pacientes em ambas</div>
                </div>
                <div style={s.statBox(P.green)}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: P.green }}>+{mergeResult.stats.adicionados.toLocaleString('pt-BR')}</div>
                  <div style={{ fontSize: '0.72rem', color: P.muted, marginTop: 3 }}>Adicionados da secundária</div>
                </div>
                <div style={s.statBox(P.purple)}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: P.purple }}>{mergeResult.stats.total.toLocaleString('pt-BR')}</div>
                  <div style={{ fontSize: '0.72rem', color: P.muted, marginTop: 3 }}>Total final</div>
                </div>
              </div>

              {mergeResult.stats.adicionados > 0 ? (
                <>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                    Prévia dos primeiros {mergeResult.preview.length} pacientes adicionados da planilha secundária:
                  </div>
                  <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #F1F5F9' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr>
                          {colNomeSecund && <th style={s.th}>Nome</th>}
                          {colNascSecund && <th style={s.th}>Data Nasc.</th>}
                          {colunasSecund.filter(c => c !== colNomeSecund && c !== colNascSecund).slice(0, 4).map(c => (
                            <th key={c} style={s.th}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mergeResult.preview.map((row, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? '#FFFFFF' : '#F8FAFC' }}>
                            {colNomeSecund && <td style={{ ...s.td, fontWeight: 600 }}>{row[colNomeSecund] || '—'}</td>}
                            {colNascSecund && <td style={s.td}>{row[colNascSecund] || '—'}</td>}
                            {colunasSecund.filter(c => c !== colNomeSecund && c !== colNascSecund).slice(0, 4).map(c => (
                              <td key={c} style={s.td} title={row[c]}>{row[c] || '—'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {mergeResult.stats.adicionados > 10 && (
                    <div style={{ fontSize: '0.78rem', color: P.muted, marginTop: 8 }}>
                      ...e mais {(mergeResult.stats.adicionados - 10).toLocaleString('pt-BR')} pacientes serão adicionados.
                    </div>
                  )}
                </>
              ) : (
                <div style={s.alert('info')}>
                  ℹ️ Todos os pacientes da planilha secundária já existem na planilha principal. Nenhum novo paciente será adicionado.
                </div>
              )}
            </div>
          )}

          {/* Preview planilha única */}
          {!modoMescla && rowsPrinc.length > 0 && (
            <div style={s.card()}>
              <div style={s.cardTitle}>👁️ Prévia — primeiros 5 de {rowsPrinc.length.toLocaleString('pt-BR')} registros</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead><tr>{colunasPrinc.map(c => <th key={c} style={s.th}>{c}</th>)}</tr></thead>
                  <tbody>
                    {rowsPrinc.slice(0, 5).map((row, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                        {colunasPrinc.map(c => <td key={c} style={s.td} title={row[c]}>{row[c] || '—'}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Botão de upload */}
          {rowsPrinc.length > 0 && (
            <div style={s.card()}>
              {uploading && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: P.muted, marginBottom: 6 }}>
                    <span>⏳ Salvando dados...</span><span>{uploadProgress}%</span>
                  </div>
                  <div style={{ height: 6, background: '#E2E8F0', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${uploadProgress}%`, background: `linear-gradient(90deg,${P.green},#059669)`, borderRadius: 999, transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}
              <button style={s.btn(!prontoParaSalvar || uploading ? '' : 'green', !prontoParaSalvar || uploading)}
                disabled={!prontoParaSalvar || uploading} onClick={handleUpload}>
                {uploading
                  ? `⏳ Salvando... ${uploadProgress}%`
                  : modoMescla && mergeResult
                    ? `📤 Salvar ${rowsParaSalvar.length.toLocaleString('pt-BR')} registros mesclados`
                    : `📤 Salvar ${rowsPrinc.length.toLocaleString('pt-BR')} registros`}
              </button>
              {modoMescla && !mergeResult && rowsSecund.length === 0 && (
                <div style={{ ...s.alert('info'), marginTop: 12, marginBottom: 0 }}>
                  ℹ️ Adicione a planilha secundária para fazer a mesclagem, ou mude para "Planilha única".
                </div>
              )}
              {modoMescla && !mergeResult && rowsSecund.length > 0 && (
                <div style={{ ...s.alert('error'), marginTop: 12, marginBottom: 0 }}>
                  ⚠️ Mapeie ao menos o <strong>Nº de Atendimento</strong> nas duas planilhas, ou o <strong>Nome + Data de Nascimento</strong> nas duas, para liberar a mesclagem.
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
